import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControlLabel,
  FormLabel,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { apiAuthBaseUrl, apiBaseUrl } from "config";
import useTranslation from "Hooks/useTranslation";
import AlertToastr from "utils/alert";

const autoLubeDatePickerPopperContainer = ({ children }) =>
  typeof document !== "undefined"
    ? ReactDOM.createPortal(children, document.body)
    : children;

const autoLubeDatePickerProps = {
  popperContainer: autoLubeDatePickerPopperContainer,
  popperClassName: "custom-datepicker-popper auto-lube-report-datepicker-popper",
};

const AutoLubeReportDialog = ({ open, onClose, companyId }) => {
  const translation = useTranslation();
  const autoLubeAllUnitsRef = useRef([]);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [licenses, setLicenses] = useState([]);
  const [licensesLoading, setLicensesLoading] = useState(false);
  const [selectedLicenseId, setSelectedLicenseId] = useState("");
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitMode, setUnitMode] = useState("all");
  const [selectedUnitUIds, setSelectedUnitUIds] = useState([]);

  useEffect(() => {
    if (!open || !companyId) return undefined;

    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setDateFrom(thirtyDaysAgo);
    setDateTo(now);
    setSelectedLicenseId("");
    setLicenses([]);
    setUnits([]);
    setUnitMode("all");
    setSelectedUnitUIds([]);
    autoLubeAllUnitsRef.current = [];

    let cancelled = false;
    setLicensesLoading(true);
    setUnitsLoading(true);

    Promise.all([
      axios.get(`${apiAuthBaseUrl}/licenses/by-companyid/${companyId}`, {
        headers: { CompanyId: companyId },
      }),
      axios.get(`${apiBaseUrl}/units/unique`, {
        headers: { CompanyId: companyId },
      }),
    ])
      .then(([licensesRes, unitsRes]) => {
        if (cancelled) return;
        const licensesList = Array.isArray(licensesRes.data) ? licensesRes.data : [];
        const allUnits = Array.isArray(unitsRes.data) ? unitsRes.data : [];
        setLicenses(licensesList);
        autoLubeAllUnitsRef.current = allUnits;
        if (licensesList.length === 1) {
          const licId = licensesList[0].id || licensesList[0].licenseId || "";
          setSelectedLicenseId(licId);
          setUnits(allUnits.filter((u) => u.licenseId === licId));
        }
      })
      .catch(() => {
        if (!cancelled) AlertToastr.showErrorAlert("Failed to load data");
      })
      .finally(() => {
        if (!cancelled) {
          setLicensesLoading(false);
          setUnitsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  const handleClose = () => {
    if (exporting) return;
    onClose();
  };

  const handleExport = async () => {
    if (!companyId || !selectedLicenseId || !dateFrom || !dateTo) return;
    setExporting(true);
    try {
      const dateFromUtc = moment(dateFrom).utc().format("YYYY-MM-DDTHH:mm:ss");
      const dateToUtc = moment(dateTo).utc().format("YYYY-MM-DDTHH:mm:ss");
      const headers = {
        "Content-Type": "application/json",
        CompanyId: companyId,
      };
      const body = {
        licenseId: selectedLicenseId,
        dateFrom: dateFromUtc,
        dateTo: dateToUtc,
      };
      if (unitMode === "select" && selectedUnitUIds.length > 0) {
        body.unitUIds = selectedUnitUIds;
      }
      const res = await axios.post(
        `${apiBaseUrl}/reports/pump-auto-lube/export`,
        body,
        { headers, responseType: "blob" },
      );
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disposition = res.headers["content-disposition"];
      const dateFromLocal = moment(dateFrom).format("DDMMMYY");
      const dateToLocal = moment(dateTo).format("DDMMMYY");
      const datePart = dateFromLocal === dateToLocal ? dateFromLocal : `${dateFromLocal}-${dateToLocal}`;
      let fileName = `AutoLubeReport_${datePart}.xlsx`;
      if (disposition) {
        const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) fileName = match[1].replace(/['"]/g, "");
      }
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Error generating report";
      AlertToastr.showErrorAlert(msg);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="respModal scrollContentModal autoHeight autoLubeReportModal datepicker-modal"
    >
      <div className="formContentHolder">
        <div className="modalTitleHolder">
          <h1 className="modalTitle text-center">Generate Auto Lube Report</h1>
        </div>
        <div className="formContent">
          <div className="formContentBlock modalFieldLabelSettings">
            <FormLabel component="legend" className="mb5 modalFieldLabel">
              License
            </FormLabel>
            <TextField
              select
              value={selectedLicenseId}
              onChange={(e) => {
                const licId = e.target.value;
                setSelectedLicenseId(licId);
                setUnits(autoLubeAllUnitsRef.current.filter((u) => u.licenseId === licId));
                setUnitMode("all");
                setSelectedUnitUIds([]);
              }}
              variant="outlined"
              margin="normal"
              fullWidth
              disabled={licensesLoading}
              SelectProps={{ displayEmpty: true }}
              InputProps={{
                endAdornment: licensesLoading ? (
                  <CircularProgress size={20} style={{ marginRight: 8 }} />
                ) : null,
              }}
            >
              <MenuItem value="" className="filterMenuItem">
                Select license
              </MenuItem>
              {licenses.map((lic) => (
                <MenuItem
                  key={lic.id || lic.licenseId}
                  value={lic.id || lic.licenseId}
                  className="filterMenuItem"
                >
                  {lic.name || lic.licenseId || lic.id}
                </MenuItem>
              ))}
            </TextField>
          </div>
          {selectedLicenseId && (
            <div className="formContentBlock modalFieldLabelSettings">
              <FormLabel component="legend" className="mb5 modalFieldLabel">
                Units
              </FormLabel>
              <div className="radioButtonGroup">
                <label className="radioButtonLabel">
                  <input
                    type="radio"
                    name="units"
                    value="all"
                    checked={unitMode === "all"}
                    onChange={() => {
                      setUnitMode("all");
                      setSelectedUnitUIds([]);
                    }}
                  />
                  All
                </label>
                <label className="radioButtonLabel">
                  <input
                    type="radio"
                    name="units"
                    value="select"
                    checked={unitMode === "select"}
                    onChange={() => setUnitMode("select")}
                  />
                  Select Unit
                </label>
              </div>
              {unitMode === "select" && (
                <div className="collapseBodyHolder">
                  <div className="checkboxListHolder mt8">
                    <div className="checkboxListContainer">
                      {unitsLoading ? (
                        <div style={{ textAlign: "center", padding: 16 }}>
                          <CircularProgress size={24} />
                        </div>
                      ) : units.length === 0 ? (
                        <div className="autoLubeReportEmptyUnits">No units found</div>
                      ) : (
                        units.map((unit) => {
                          const uid = unit.uId;
                          const label = unit.nameLocalized || unit.nameUser || unit.name || unit.code || uid;
                          return (
                            <FormControlLabel
                              key={uid}
                              control={
                                <Checkbox
                                  color="default"
                                  name={uid}
                                  checked={selectedUnitUIds.includes(uid)}
                                  onChange={(e) => {
                                    setSelectedUnitUIds((prev) =>
                                      e.target.checked
                                        ? [...prev, uid]
                                        : prev.filter((id) => id !== uid),
                                    );
                                  }}
                                />
                              }
                              label={label}
                            />
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="formContentBlock modalFieldLabelSettings autoLubeReportDateFields">
            <div className="autoLubeReportDateField">
              <FormLabel component="legend" className="mb5 modalFieldLabel">
                {translation.fromLabel}
              </FormLabel>
              <div className="DateTimePickerFrom" style={{ paddingBottom: 0 }}>
                <DatePicker
                  selected={dateFrom}
                  onChange={setDateFrom}
                  showTimeSelect
                  dateFormat="Pp"
                  className="formControlDatePicker"
                  timeFormat="hh:mm aa"
                  {...autoLubeDatePickerProps}
                />
              </div>
            </div>
            <div className="autoLubeReportDateField">
              <FormLabel component="legend" className="mb5 modalFieldLabel">
                {translation.toLabel}
              </FormLabel>
              <div className="DateTimePickerTo" style={{ paddingBottom: 0 }}>
                <DatePicker
                  selected={dateTo}
                  onChange={setDateTo}
                  showTimeSelect
                  dateFormat="Pp"
                  className="formControlDatePicker"
                  timeFormat="hh:mm aa"
                  {...autoLubeDatePickerProps}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="btnHolder end fixedWidthBtn">
          <Button variant="outlined" onClick={handleClose} disabled={exporting}>
            {translation.cancelButtonLabel}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedLicenseId || !dateFrom || !dateTo || exporting || licensesLoading}
            onClick={handleExport}
            id="autoLubeReportExportBtn"
          >
            {exporting ? <CircularProgress size={20} color="inherit" /> : translation.exportButtonLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default AutoLubeReportDialog;
